import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { ApiError } from 'types/api/ApiError';
import { GetTransportTypesResponse } from 'types/api/getTransportTypes';
import captureException from 'utils/captureException';

export type TransportType = {
  value: string;
  label: string;
};

// Type guard to check if value is a record/object
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Normalize API response to our expected format
function normalizeTransportTypesResponse(data: unknown): {
  transportTypes: TransportType[];
} {
  // If response is an array directly
  if (Array.isArray(data)) {
    return {
      transportTypes: data.map((item) => {
        const itemObj = isRecord(item) ? item : {};
        return {
          value:
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            (typeof itemObj.id === 'string' ? itemObj.id : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            String(item),
          label:
            (typeof itemObj.label === 'string' ? itemObj.label : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            String(item),
        };
      }),
    };
  }

  // If response has transportTypes field
  if (isRecord(data) && Array.isArray(data.transportTypes)) {
    return {
      transportTypes: data.transportTypes.map((item) => {
        const itemObj = isRecord(item) ? item : {};
        return {
          value:
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            (typeof itemObj.id === 'string' ? itemObj.id : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            String(item),
          label:
            (typeof itemObj.label === 'string' ? itemObj.label : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            String(item),
        };
      }),
    };
  }

  // If response has data field
  if (isRecord(data) && Array.isArray(data.data)) {
    return {
      transportTypes: data.data.map((item) => {
        const itemObj = isRecord(item) ? item : {};
        return {
          value:
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            (typeof itemObj.id === 'string' ? itemObj.id : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            String(item),
          label:
            (typeof itemObj.label === 'string' ? itemObj.label : '') ||
            (typeof itemObj.name === 'string' ? itemObj.name : '') ||
            (typeof itemObj.value === 'string' ? itemObj.value : '') ||
            String(item),
        };
      }),
    };
  }

  // Default: return empty array
  return { transportTypes: [] };
}

export async function getTransportTypes(
  tmsID: number,
  forceRefresh?: boolean
): Promise<Result<{ transportTypes: TransportType[] }, ApiError>> {
  try {
    const queryParams = `tmsID=${tmsID}${forceRefresh ? '&forceRefresh=true' : ''}`;
    const response = await axios.get<GetTransportTypesResponse>(
      `/transport-types/?${queryParams}`
    );
    const normalized = normalizeTransportTypesResponse(response.data);
    return ok(normalized);
  } catch (error) {
    captureException(error, { functionName: 'getTransportTypes' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get transport types from TMS' });
    }

    return err({
      message:
        error.response?.data.message ||
        'Failed to get transport types from TMS',
    });
  }
}
